import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Card, Button, Modal, Form } from "react-bootstrap";
import { Formik } from "formik";
import {
  CalendarDays,
  MapPin,
  Palette,
  Shirt,
  Droplets,
  Users,
  ListTodo,
  Layers,
  Image as ImageIcon,
  UserRound,
} from "lucide-react";
import { editUserEvents, getEventById } from "../../services/UserServices/UserService";
import toast from "react-hot-toast";
import { EVENT_NAME } from "../../utils/events";
import PageTitle from "../layouts/PageTitle";
import { PageContentLoader } from "../components/TableLoader";

const EventCard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const historyData = location.state?.tension;
  const [isGalleryExists, setIsGalleryExists] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [event, setEvent] = useState();
  const [loading, setLoading] = useState(true);
  const { id } = useParams();

  const mapSingleEvent = (eventData) => {
    if (!eventData) return null;

    const matchEvent = EVENT_NAME.find((item) => item.id === eventData?.name?.id);

    if (!matchEvent) {
      return {
        id: eventData?.name?.id || "custom",
        name: eventData?.name?.name || "Custom Event",
        eventId: eventData._id,
        venue: eventData?.eventVenue,
        date: eventData?.eventDate,
        subEvents: eventData?.subEvents || [],
        location: eventData?.location || "",
        createdBy: eventData?.createdBy || "",
        selectedTheme: eventData.theme,
        selectedDressCode: eventData.dressCode,
        selectedDressColor: eventData.dressColor,
        theme: [],
        dressCode: [],
        dressColor: [],
      };
    }

    return {
      ...matchEvent,
      name: eventData?.name?.name || matchEvent.name,
      eventId: eventData._id,
      venue: eventData?.eventVenue,
      date: eventData?.eventDate,
      subEvents: eventData?.subEvents || [],
      location: eventData?.location || "",
      createdBy: eventData?.createdBy || "",
      selectedTheme:
        eventData.theme?.id === "custom"
          ? { id: "custom", name: eventData.theme?.name || "Custom" }
          : matchEvent.theme.find((t) => t.id === eventData.theme?.id) || eventData.theme,
      selectedDressCode:
        eventData.dressCode?.id === "custom"
          ? { id: "custom", name: eventData.dressCode?.name || "Custom" }
          : matchEvent.dressCode.find((dc) => dc.id === eventData.dressCode?.id) ||
            eventData.dressCode,
      selectedDressColor:
        eventData.dressColor?.id === "custom"
          ? { id: "custom", name: eventData.dressColor?.name || "Custom" }
          : matchEvent.dressColor.find((col) => col.id === eventData.dressColor?.id) ||
            eventData.dressColor,
    };
  };

  const getEvents = async () => {
    try {
      setLoading(true);
      const response = await getEventById(id);
      setIsGalleryExists(response?.data?.data?.isGalleryExists);
      const mappedEvents = mapSingleEvent(response?.data?.data?.event);
      setEvent(mappedEvents);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getEvents();
  }, []);

  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleString("en-IN", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
      : "—";

  const hostName = event?.createdBy?.hostName
    ? `${event.createdBy.hostName.charAt(0).toUpperCase()}${event.createdBy.hostName.slice(1)}`
    : null;

  const detailFields = [
    {
      label: "Event Date / Time",
      value: formatDate(event?.date),
      icon: CalendarDays,
    },
    {
      label: "Venue",
      value: event?.venue || "N/A",
      icon: MapPin,
    },
    {
      label: "Theme",
      value: event?.selectedTheme?.name || "N/A",
      icon: Palette,
    },
    {
      label: "Dress Code",
      value: event?.selectedDressCode?.name || "N/A",
      icon: Shirt,
    },
    {
      label: "Dress Color",
      value: event?.selectedDressColor?.name || "N/A",
      icon: Droplets,
    },
    {
      label: "Location",
      value: event?.location || "N/A",
      icon: MapPin,
      wide: true,
    },
  ];

  const actions = [
    {
      label: "Sub Events",
      icon: Layers,
      onClick: () =>
        navigate(`/event_details/${event?.eventId}/sub-events`, { state: event }),
    },
    {
      label: "Tasks",
      icon: ListTodo,
      onClick: () => navigate(`/event_details/${event?.eventId}/tasks`, { state: event }),
    },
    {
      label: "Guest Listings",
      icon: Users,
      onClick: () => navigate(`/event_details/${event?.eventId}/guests`, { state: event }),
    },
    ...(isGalleryExists
      ? [
          {
            label: "Photos",
            icon: ImageIcon,
            onClick: () => navigate(`/photo-gallery/${event?.eventId}`, { state: event }),
          },
        ]
      : []),
  ];

  return (
    <div className="qx-page qx-detail-page">
      <PageTitle
        breadcrumbs={[
          { label: "Event List", path: "/events" },
          { label: historyData?.name ? historyData?.name : "Event" },
        ]}
      />

      {loading ? (
        <PageContentLoader message="Loading event details..." />
      ) : !event ? (
        <div className="qx-empty-state">
          <p className="mb-1" style={{ fontWeight: 600, color: "#27272a" }}>
            Event not found
          </p>
          <p className="mb-0">This event may have been removed or is unavailable.</p>
        </div>
      ) : (
        <Card className="qx-event-detail-card mb-4">
          <Card.Body className="p-4">
            <div className="qx-event-detail-head">
              <div>
                <p className="qx-event-detail-kicker mb-1">Event details</p>
                <h2 className="qx-event-detail-title mb-0">{event?.name || "Event"}</h2>
              </div>
              {hostName && (
                <button
                  type="button"
                  className="qx-event-host-chip"
                  onClick={() => navigate(`/user-details/${event?.createdBy?._id}`)}
                >
                  <UserRound size={16} />
                  <span>
                    Created by <strong>{hostName}</strong>
                  </span>
                </button>
              )}
            </div>

            <div className="qx-event-detail-grid">
              {detailFields.map((field) => {
                const Icon = field.icon;
                return (
                  <div
                    key={field.label}
                    className={`qx-detail-field${field.wide ? " qx-detail-field--wide" : ""}`}
                  >
                    <span className="qx-detail-field__label">
                      <Icon size={12} className="me-1" />
                      {field.label}
                    </span>
                    <span className="qx-detail-field__value">{field.value}</span>
                  </div>
                );
              })}
            </div>

            <div className="qx-event-actions">
              {actions.map((action) => {
                const Icon = action.icon;
                return (
                  <button
                    key={action.label}
                    type="button"
                    className="qx-event-action-btn"
                    onClick={action.onClick}
                  >
                    <Icon size={16} />
                    {action.label}
                  </button>
                );
              })}
            </div>
          </Card.Body>
        </Card>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Event</Modal.Title>
        </Modal.Header>

        <Formik
          initialValues={{
            eventId: event?._id,
            name: {
              name: event?.name?.name || "",
              id: event?.name?.id,
            },
            theme: {
              name: event?.theme?.name || "",
              id: event?.theme?.id,
            },
            dressCode: {
              name: event?.dressCode?.name || "",
              id: event?.dressCode?.id,
            },
            dressColor: {
              name: event?.dressColor?.name || "",
              id: event?.dressColor?.id,
            },
          }}
          onSubmit={async (values) => {
            try {
              await editUserEvents(values);
              toast.success("event updated successfully");
              setShowModal(false);
            } catch (error) {
              console.error(error);
            }
          }}
        >
          {({ handleSubmit, handleChange, values, errors, touched }) => (
            <Form onSubmit={handleSubmit} className="p-3">
              <Form.Group className="mb-3">
                <Form.Label>Event Name</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={values?.name?.name}
                  onChange={handleChange}
                  isInvalid={touched?.name && errors?.name?.name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors?.name?.name}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Theme</Form.Label>
                <Form.Control
                  type="text"
                  name="theme"
                  value={values?.theme?.name}
                  onChange={handleChange}
                  isInvalid={touched?.theme?.name && errors?.theme?.name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors?.theme?.name}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Dress Code</Form.Label>
                <Form.Control
                  type="text"
                  name="dressCode"
                  value={values?.dressCode?.name}
                  onChange={handleChange}
                  isInvalid={touched?.dressCode?.name && errors?.dressCode?.name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors?.dressCode?.name}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Dress Color</Form.Label>
                <Form.Control
                  type="text"
                  name="dressColor"
                  value={values?.dressColor?.name}
                  onChange={handleChange}
                  isInvalid={touched?.dressColor?.name && errors?.dressColor?.name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors?.dressColor?.name}
                </Form.Control.Feedback>
              </Form.Group>

              <div className="d-flex justify-content-end mt-3">
                <Button variant="secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="ms-2"
                  style={{ backgroundColor: "#A60000", border: "none" }}
                >
                  Save Changes
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal>
    </div>
  );
};

export default EventCard;
